import { useParams } from 'react-router-dom'
import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import {
  InvestigationSpine,
  type InvestigationTab,
} from '@/components/layouts/InvestigationSpine'
import type { Vulnerability } from '@/lib/types'
import { VulnContextStrip } from '@/components/detail/VulnContextStrip'
import { DetailActionBar } from '@/components/detail/DetailActionBar'
import {
  ExploreTab,
  type Reference,
  type ExtraSection,
  type SourceSummary,
} from '@/components/detail/ExploreTab'
import { TimelineTab, type TimelineEvent } from '@/components/detail/TimelineTab'
import { NotesPanel } from '@/components/shared/NotesPanel'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { useVulnerability, useArticles } from '@/hooks/queries'
import { formatDate } from '@/lib/utils'
import { normaliseReferences } from '@/lib/references'
import {
  isV3RemediationEnabled,
  isV5AdvisoriesEnabled,
  isV6EnrichmentEnabled,
} from '@/lib/flags'
import { RemediationSection } from '@/components/vulnerabilities/RemediationSection'
import { ActiveExploitationCard } from '@/components/vulnerabilities/ActiveExploitationCard'
import { AffectedProductsCard } from '@/components/vulnerabilities/AffectedProductsCard'
import { AffectedVendorsBadges } from '@/components/vulnerabilities/AffectedVendorsBadges'
import { AttackAndWeaknessesCard } from '@/components/vulnerabilities/AttackAndWeaknessesCard'
import { DiscussCTA } from '@/components/community/DiscussCTA'
import { ChannelCTA } from '@/components/community/ChannelCTA'
import { LeakSiteGroupsForCveCard } from '@/components/leak-sites/LeakSiteGroupsForCveCard'
import {
  SupplyChainIncidentsForCveCard,
  supplyChainByCveQueryOptions,
} from '@/components/supply-chain/SupplyChainIncidentsForCveCard'
import { useQuery } from '@tanstack/react-query'
import { resolveVendorsForDisplay } from '@/lib/vendor-display'

export function VulnerabilityDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: vuln, isLoading } = useVulnerability(id)
  const { data: relatedArticles } = useArticles(
    vuln ? { search: vuln.cveId } : undefined,
  )
  // Shared with `<SupplyChainIncidentsForCveCard>` (same query key) so the
  // CVE detail page makes a single round-trip to `/api/supply-chain/by-cve/:id`
  // for both the cross-ref card AND the vendor-display fallback chain.
  // Disabled until `vuln.cveId` is known so we don't fire a useless request.
  const { data: supplyChainByCve } = useQuery({
    ...supplyChainByCveQueryOptions(vuln?.cveId ?? ''),
    enabled: Boolean(vuln?.cveId),
  })
  const [watched, setWatched] = useState(false)

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }
  if (!vuln) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Vulnerability not found.</p>
      </div>
    )
  }

  // References: heterogeneous shapes (string[], object-of-strings, structured objects) — normalise to Reference[]
  const exploreReferences: Reference[] = normaliseReferences(vuln.references)

  const affectedProducts: string[] = Array.isArray(vuln.affectedProducts)
    ? (vuln.affectedProducts as string[])
    : vuln.affectedProducts && typeof vuln.affectedProducts === 'object'
      ? Object.values(vuln.affectedProducts as Record<string, string>)
      : []
  const sources: SourceSummary[] = (relatedArticles ?? [])
    .slice(0, 5)
    .map((a) => ({ id: a.id, title: a.publisher }))

  // T17: Community Discuss + Channel CTAs near the top of the Explore tab.
  // CVE entityId is the CVE-XXXX string (per T4 schema drift), not a uuid.
  // ontologyTags are aggregated server-side from every article that mentions
  // this CVE (articles.related_cves overlap). When no related article carries
  // the relevant taxonomy the bag is empty and `<ChannelCTA>` silently
  // renders nothing.
  const extraSections: ExtraSection[] = [
    {
      id: 'community-cta',
      title: 'Community',
      content: (
        <div className="entity-cta-row flex flex-wrap items-center gap-3">
          <DiscussCTA entityType="cve" entityId={vuln.cveId} />
          <ChannelCTA ontologyTags={vuln.ontologyTags ?? {}} />
        </div>
      ),
    },
  ]
  if (vuln.cvssVector) {
    extraSections.push({
      id: 'cvss-vector',
      title: 'CVSS Vector',
      content: (
        <code className="text-xs bg-muted rounded px-2 py-1">
          {vuln.cvssVector}
        </code>
      ),
    })
  }
  // Phase 2 T9+T12 — replace the legacy "Affected Products / Vendors"
  // section with proper clickable vendor badges. The legacy
  // `affectedProducts` jsonb column had ~1 populated row in prod and was
  // a glorified string bag — vendors live on `affected_vendors uuid[]`
  // (FK to `vendors.id`) and the API returns them as `VendorRef[]` shaped
  // `{id, name, tier, aliasOf?}` (Phase 2 namespace).
  //
  // ~688/8286 CVEs have an empty `affected_vendors` array even when other
  // vendor signals exist (e.g. CVE-2026-35431 — Microsoft Entra ID — has
  // supply-chain incidents tagged "Microsoft Azure" and MSRC advisories
  // but the canonical column is empty). `resolveVendorsForDisplay` falls
  // through to those secondary signals, marking the result with a small
  // provenance pill so the user knows it's not the canonical signal.
  // Once Phase 3a populates more rows from NVD CPE data, those vendors
  // land in the canonical tier-1 field and the fallback transparently
  // supersedes — no UI change needed.
  const displayVendors = resolveVendorsForDisplay(
    vuln,
    supplyChainByCve?.incidents,
  )
  if (displayVendors.length > 0) {
    extraSections.push({
      id: 'affected-vendors',
      title: 'Affected Vendors',
      content: <AffectedVendorsBadges vendors={displayVendors} />,
    })
  }
  // The `affectedProducts` array remains read for back-compat. Render it
  // ONLY when the resolved fallback chain produced no vendors AND the
  // legacy bag has data — buys a graceful read of the historical row
  // without surfacing duplicate signal once Phase B's projector lands.
  if (displayVendors.length === 0 && affectedProducts.length > 0) {
    extraSections.push({
      id: 'affected-products',
      title: 'Affected Products',
      content: (
        <div className="flex flex-wrap gap-2">
          {affectedProducts.map((p, i) => (
            <Badge key={i} variant="secondary">
              {String(p)}
            </Badge>
          ))}
        </div>
      ),
    })
  }
  if (vuln.patchUrl) {
    extraSections.push({
      id: 'patch',
      title: 'Patch',
      content: (
        <a
          href={vuln.patchUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-primary hover:underline break-all"
        >
          {vuln.patchUrl}
        </a>
      ),
    })
  }
  // V6 — CIRCL enrichment cards (ActiveExploitation, AffectedProducts,
  // AttackAndWeaknesses). Flag-gated — the CIRCL adapter (Slice 2b) hasn't
  // backfilled every CVE yet. The API now always projects an `enrichment`
  // sub-object (see vulnerabilities.routes.ts Slice 3), so individual cards
  // self-hide when their data slice is empty.
  if (isV6EnrichmentEnabled() && vuln.enrichment) {
    const { exploits, cpes, techniques, cwes } = vuln.enrichment
    const anyData =
      exploits.length > 0 ||
      cpes.length > 0 ||
      techniques.length > 0 ||
      cwes.length > 0
    if (anyData) {
      extraSections.push({
        id: 'v6-circl-enrichment',
        title: 'CIRCL enrichment',
        content: (
          <div className="space-y-0">
            <ActiveExploitationCard exploits={exploits} />
            <AffectedProductsCard cpes={cpes} />
            <AttackAndWeaknessesCard techniques={techniques} cwes={cwes} />
          </div>
        ),
      })
    }
  }

  // Plan C Plan 2 T20 — Supply-chain incidents that reference this CVE.
  // Self-hides on empty so CVEs without supply-chain cross-references stay
  // clean. Wraps `GET /api/supply-chain/by-cve/:cveId`.
  extraSections.push({
    id: 'supply-chain-incidents-for-cve',
    title: 'Supply-chain incidents',
    content: <SupplyChainIncidentsForCveCard cveId={vuln.cveId} />,
  })

  // V3 — Remediation section (CISO-facing patch classifier signal). Flag-gated
  // because the classifier hasn't backfilled every CVE yet; once Slice 2's
  // pipeline adapter runs over prod data this is safe to turn on permanently.
  if (isV3RemediationEnabled() && vuln.patchClassifiedAt) {
    extraSections.push({
      id: 'remediation',
      title: 'Remediation',
      content: (
        <RemediationSection
          patchAvailable={vuln.patchAvailable ?? null}
          fixVersionHint={vuln.fixVersionHint ?? null}
          primaryAdvisoryUrl={vuln.primaryAdvisoryUrl ?? null}
          patchClassifiedAt={vuln.patchClassifiedAt}
          vendorAdvisories={
            isV5AdvisoriesEnabled() ? vuln.vendorAdvisories : undefined
          }
        />
      ),
    })
  }

  // Admin context — renders collapsed below main sections when any admin
  // signal is present on the CVE. Provenance fields + adminNotes arrive via
  // Slice 2's public `GET /api/vulnerabilities/:id` response projection.
  if (
    vuln.adminNotes ||
    vuln.exploitationStatusChangedBy ||
    vuln.patchAvailableChangedBy
  ) {
    extraSections.push({
      id: 'admin-context',
      title: 'Admin context',
      content: <AdminContextSection vuln={vuln} />,
    })
  }

  // Related Articles must sit at the absolute bottom of the Explore tab —
  // below References + Sources + Notes (which `<ExploreTab>` renders in its
  // own footer after `extraSections`). The simplest non-invasive fix is to
  // tuck Related Articles into the `notesSlot` AFTER `<NotesPanel>` rather
  // than pushing it onto `extraSections` (which renders mid-page). This keeps
  // `<ExploreTab>` generic — other pages (ThreatActorDetailPage) keep their
  // current ordering.
  const relatedArticlesSection =
    relatedArticles && relatedArticles.length > 0 ? (
      <section
        aria-labelledby="vuln-related-articles-heading"
        className="flex flex-col gap-2 mt-6"
      >
        <h3
          id="vuln-related-articles-heading"
          className="text-sm font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Related Articles
        </h3>
        <div className="space-y-2">
          {relatedArticles.slice(0, 5).map((a) => (
            <div key={a.id} className="rounded-md border bg-card p-3">
              <p className="text-sm font-medium">{a.title}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {a.publisher} · {formatDate(a.publishedAt)}
              </p>
            </div>
          ))}
        </div>
      </section>
    ) : null

  // Synthesise timeline events from sparse fields
  const events: TimelineEvent[] = []
  if (vuln.firstSeen) {
    events.push({
      id: 'first-seen',
      at: vuln.firstSeen,
      kind: 'first-seen',
      label: 'First seen',
    })
  }
  if (vuln.lastUpdated) {
    events.push({
      id: 'last-updated',
      at: vuln.lastUpdated,
      kind: 'update',
      label: 'Last updated',
    })
  }
  if (vuln.patchAvailable && vuln.patchUrl) {
    events.push({
      id: 'patch',
      at: vuln.lastUpdated ?? vuln.firstSeen ?? vuln.createdAt,
      kind: 'patch',
      label: 'Patch available',
      detail: vuln.patchUrl,
    })
  }
  ;(relatedArticles ?? []).forEach((a) => {
    events.push({
      id: `article-${a.id}`,
      at: a.publishedAt,
      kind: 'mention',
      label: `${a.publisher}: ${a.title}`,
    })
  })

  const tabs: InvestigationTab[] = [
    {
      id: 'explore',
      label: 'Explore',
      content: (
        <ExploreTab
          summary={vuln.description ?? undefined}
          extraSections={extraSections}
          references={exploreReferences}
          sources={sources}
          notesSlot={
            <>
              {/* Plan B Plan 2 T18: ransomware groups referencing this CVE.
                  Self-hides when the API returns an empty list. */}
              <LeakSiteGroupsForCveCard cveId={vuln.cveId} />
              <NotesPanel entityType="vulnerability" entityId={vuln.id} />
              {relatedArticlesSection}
            </>
          }
        />
      ),
    },
    {
      id: 'timeline',
      label: 'Timeline',
      content: <TimelineTab events={events} />,
    },
  ]

  return (
    <InvestigationSpine
      title={vuln.cveId}
      contextStrip={
        <VulnContextStrip vuln={vuln} displayVendors={displayVendors} />
      }
      actionBar={
        <DetailActionBar
          url={typeof window !== 'undefined' ? window.location.href : ''}
          watched={watched}
          onToggleWatch={setWatched}
          entity={{ type: 'Vulnerability', name: vuln.cveId, slug: vuln.cveId }}
        />
      }
      tabs={tabs}
    />
  )
}

/**
 * CISO-facing "Admin context" collapsible section.
 *
 * Renders only when one of: adminNotes, exploitationStatusChangedBy,
 * patchAvailableChangedBy is set on the vulnerability. Read-only — all edits
 * happen on the admin side via `/admin/vulnerabilities/:id/edit`.
 *
 * The field names + shapes match `Vulnerability` in `apps/web/src/lib/types.ts`
 * (optional, populated by Slice 2's public response projection).
 */
function AdminContextSection({ vuln }: { vuln: Vulnerability }) {
  const [open, setOpen] = useState(false)
  return (
    <section className="rounded-md border border-border bg-muted/20 p-3 text-sm">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={open}
        aria-controls="admin-context-body"
      >
        <span className="font-semibold">Admin context</span>
        {open ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" aria-hidden />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden />
        )}
      </button>
      {open && (
        <div id="admin-context-body" className="mt-3 flex flex-col gap-3 text-xs">
          {vuln.adminNotes && (
            <div>
              <p className="mb-1 font-medium text-foreground">Notes</p>
              <p className="whitespace-pre-wrap text-muted-foreground">
                {vuln.adminNotes}
              </p>
            </div>
          )}
          {vuln.exploitationStatusChangedBy && (
            <p className="text-muted-foreground">
              Exploit status source:{' '}
              <span className="font-mono text-foreground">
                {vuln.exploitationStatusChangedBy}
              </span>
              {vuln.exploitationStatusChangedAt && (
                <> · {formatDate(vuln.exploitationStatusChangedAt)}</>
              )}
            </p>
          )}
          {vuln.patchAvailableChangedBy && (
            <p className="text-muted-foreground">
              Patch status source:{' '}
              <span className="font-mono text-foreground">
                {vuln.patchAvailableChangedBy}
              </span>
              {vuln.patchAvailableChangedAt && (
                <> · {formatDate(vuln.patchAvailableChangedAt)}</>
              )}
            </p>
          )}
        </div>
      )}
    </section>
  )
}
